import { Currency } from "./constants/connect-v2-currency.js";
import { CURRENCIES } from "./constants/currencies.js";
import { CurrencyCode } from "./constants/currency-codes.js";
import safeNumberFormat from "./safe-number-format.js";
function isMoneyTypeGuard(money) {
  return typeof money === "object" && money !== null && "amount" in money && money.amount !== void 0 && "currencyCode" in money && money.currencyCode !== void 0;
}
function isConnectV2MoneyTypeGuard(money) {
  return typeof money === "object" && money !== null && "amount" in money && money.amount !== void 0 && "currency" in money && money.currency !== void 0;
}
function isMoney(money) {
  return isMoneyTypeGuard(money) || isConnectV2MoneyTypeGuard(money);
}
function formatCurrency(amount, currencyCode, locale, options) {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    currencySign: "accounting",
    ...options
  }).format(amount);
}
function formatCurrencySafe(amount, currencyCode, locale, options) {
  return safeNumberFormat(amount, locale, {
    style: "currency",
    currency: currencyCode,
    currencySign: "accounting",
    ...options
  });
}
function formatMoney(money, locale, options, compatibilityMode = false) {
  if (typeof money.amount === "undefined") {
    throw new Error("Invalid money amount");
  }
  const currencyCodeString = extractCurrencyCode(money);
  if (typeof currencyCodeString === "undefined") {
    throw new Error("Invalid currency code");
  }
  const currencyConfig = CURRENCIES[currencyCodeString];
  const currencyDecimals = currencyConfig ? currencyConfig.decimals : 2;
  const denominator = 10 ** currencyDecimals;
  const decimalAmount = money.amount / denominator;
  return compatibilityMode ? formatCurrencySafe(decimalAmount, currencyCodeString, locale, options) : formatCurrency(decimalAmount, currencyCodeString, locale, options);
}
function extractCurrencyCode(money) {
  if (isMoneyTypeGuard(money)) {
    const { currencyCode } = money;
    return typeof currencyCode === "string" ? currencyCode : CurrencyCode[currencyCode];
  }
  if (isConnectV2MoneyTypeGuard(money)) {
    const { currency } = money;
    return typeof currency === "string" ? currency : Currency[currency];
  }
  return void 0;
}
function formatFractionalMoney(money, locale, options) {
  const { amount, currencyCode } = money;
  return formatMoney({ amount: (amount ?? 0) / 1e3, currencyCode }, locale, {
    // Need 5 decimal places because amount is in cents (2 places) and then divided by 1000 (3 places) for fractional costs
    maximumFractionDigits: 5,
    ...options
  });
}
function compareMoney(a, b) {
  if (!isMoney(a) || !isMoney(b) || typeof a.amount === "undefined" || typeof b.amount === "undefined") {
    throw new Error("Invalid money object");
  }
  const currencyA = extractCurrencyCode(a);
  const currencyB = extractCurrencyCode(b);
  if (currencyA !== currencyB) {
    throw new Error(`Cannot compare different currencies: ${currencyA} and ${currencyB}`);
  }
  return Math.sign(a.amount - b.amount);
}
export {
  compareMoney,
  formatCurrency,
  formatCurrencySafe,
  formatFractionalMoney,
  formatMoney,
  isMoney
};
//# sourceMappingURL=format-money.js.map
